import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  Search, 
  FileText,
  Package,
  Clock,
  CheckCircle,
  AlertTriangle,
  Download,
  Filter,
  RefreshCw,
  ExternalLink,
  DollarSign,
  Calendar,
  BarChart3,
  Factory,
  Plus,
  Settings2,
  X,
  TrendingUp,
  Layers,
  Box,
  Wrench,
  ClipboardList,
  User,
  Edit,
  Copy,
  Archive,
  ChevronRight,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigation } from "@/components/layout/app-layout";
import { HelpButton } from "@/components/help-button";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";
import { AdvancedFiltersModal } from "@/components/advanced-filters-modal";
import { FilterRow, FilterLogic, ActiveFilterChip } from "@/types/filters";
import { applyFilters, generateFilterChips } from "@/lib/filter-utils";
import { ConfigureColumnsModal, ColumnDefinition, ColumnConfig } from "@/components/configure-columns-modal";
import {
  exportToCSV,
  exportToExcel,
  getCurrentDateForFilename,
} from "@/lib/export-utils";
import { BOM, BOMComponent, BOMRouting } from "@/types/bom";
import { MOCK_BOMS } from "@/data/mockBOMs";

export function BillsOfMaterialPage() {
  const { navigateTo } = useNavigation();
  const { isHelpOpen, openHelp, closeHelp } = useContextualHelp("boms");
  
  // State
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [selectedBOM, setSelectedBOM] = useState<BOM | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);
  const [filterRows, setFilterRows] = useState<FilterRow[]>([]);
  const [filterLogic, setFilterLogic] = useState<FilterLogic>("AND");

  // Column configuration
  const DEFAULT_VISIBLE_COLUMNS = [
    "bomNumber",
    "parentItemCode",
    "parentItemName",
    "version",
    "status",
    "bomType",
    "totalCost",
    "componentCount",
    "effectiveDate",
  ];

  const [columnConfig, setColumnConfig] = useState<ColumnConfig>({
    visibleColumns: DEFAULT_VISIBLE_COLUMNS,
    columnOrder: DEFAULT_VISIBLE_COLUMNS,
  });

  const columnDefinitions: ColumnDefinition[] = [
    { id: "bomNumber", name: "BOM Number", description: "BOM reference number" },
    { id: "parentItemCode", name: "Parent Item Code", description: "Parent item stock code" },
    { id: "parentItemName", name: "Parent Item Name", description: "Parent item description" },
    { id: "version", name: "Version", description: "BOM version" },
    { id: "status", name: "Status", description: "BOM status" },
    { id: "bomType", name: "Type", description: "BOM type" },
    { id: "totalCost", name: "Total Cost", description: "Total BOM cost" },
    { id: "componentCount", name: "Components", description: "Number of components" },
    { id: "effectiveDate", name: "Effective Date", description: "Date BOM becomes effective" },
    { id: "lastUsedDate", name: "Last Used", description: "Last usage date" },
    { id: "usageCount", name: "Usage Count", description: "Number of times used" },
  ];

  // Filter and search BOMs
  const filteredBOMs = useMemo(() => {
    let filtered = MOCK_BOMS;

    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        bom =>
          bom.bomNumber.toLowerCase().includes(search) ||
          bom.parentItemCode.toLowerCase().includes(search) ||
          bom.parentItemName.toLowerCase().includes(search) ||
          bom.parentItemDescription.toLowerCase().includes(search)
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(bom => bom.status === statusFilter);
    }

    // Apply type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter(bom => bom.bomType === typeFilter);
    }

    // Apply advanced filters
    if (filterRows.length > 0) {
      filtered = applyFilters(filtered, filterRows, [filterLogic]);
    }

    return filtered;
  }, [searchTerm, statusFilter, typeFilter, filterRows, filterLogic]);

  // Calculate metrics
  const metrics = useMemo(() => {
    const activeBOMs = MOCK_BOMS.filter(b => b.status === "active").length;
    const draftBOMs = MOCK_BOMS.filter(b => b.status === "draft").length;
    const totalComponents = MOCK_BOMS.reduce((sum, b) => sum + b.components.length, 0);
    const avgComponentsPerBOM = totalComponents / MOCK_BOMS.length;
    const multiLevelBOMs = MOCK_BOMS.filter(b => b.isMultiLevel).length;

    return {
      total: MOCK_BOMS.length,
      active: activeBOMs,
      draft: draftBOMs,
      avgComponents: avgComponentsPerBOM.toFixed(1),
      multiLevel: multiLevelBOMs,
    };
  }, []);

  // Generate filter chips
  const activeFilterChips = useMemo(() => {
    const chips: ActiveFilterChip[] = [];
    
    if (statusFilter !== "all") {
      chips.push({
        id: "status",
        field: "status",
        operator: "equals",
        value: statusFilter,
        displayText: `Status: ${statusFilter}`,
      });
    }
    
    if (typeFilter !== "all") {
      chips.push({
        id: "type",
        field: "type",
        operator: "equals",
        value: typeFilter,
        displayText: `Type: ${typeFilter}`,
      });
    }

    if (filterRows.length > 0) {
      chips.push(...generateFilterChips(filterRows, [filterLogic]));
    }

    return chips;
  }, [statusFilter, typeFilter, filterRows, filterLogic]);

  // Handlers
  const handleViewBOM = (bom: BOM) => {
    setSelectedBOM(bom);
    setIsDrawerOpen(true);
  };

  const handleExportCSV = () => {
    const data = filteredBOMs.map(bom => ({
      "BOM Number": bom.bomNumber,
      "Parent Item Code": bom.parentItemCode,
      "Parent Item Name": bom.parentItemName,
      "Version": bom.version,
      "Status": bom.status,
      "Type": bom.bomType,
      "Total Cost": bom.totalCost.toFixed(2),
      "Components": bom.components.length,
      "Effective Date": bom.effectiveDate.toLocaleDateString(),
      "Usage Count": bom.usageCount || 0,
    }));
    
    exportToCSV(data, `boms-${getCurrentDateForFilename()}`, "BOMs");
    toast.success("BOMs exported to CSV");
  };

  const handleExportExcel = () => {
    const data = filteredBOMs.map(bom => ({
      "BOM Number": bom.bomNumber,
      "Parent Item Code": bom.parentItemCode,
      "Parent Item Name": bom.parentItemName,
      "Version": bom.version,
      "Status": bom.status,
      "Type": bom.bomType,
      "Total Cost": bom.totalCost,
      "Components": bom.components.length,
      "Effective Date": bom.effectiveDate,
      "Usage Count": bom.usageCount || 0,
    }));
    
    exportToExcel(data, `boms-${getCurrentDateForFilename()}`, "BOMs");
    toast.success("BOMs exported to Excel");
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: "default" | "secondary" | "destructive" | "outline", className: string }> = {
      active: { variant: "default", className: "bg-green-100 text-green-800 border-green-200" },
      draft: { variant: "secondary", className: "bg-yellow-100 text-yellow-800 border-yellow-200" },
      inactive: { variant: "outline", className: "bg-gray-100 text-gray-600 border-gray-200" },
      archived: { variant: "outline", className: "bg-gray-100 text-gray-500 border-gray-200" },
    };

    const config = variants[status] || variants.draft;
    return (
      <Badge variant={config.variant} className={config.className}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getTypeBadge = (type: string) => {
    const icons: Record<string, React.ReactNode> = {
      manufacturing: <Factory className="h-3 w-3 mr-1" />,
      assembly: <Box className="h-3 w-3 mr-1" />,
      kit: <Package className="h-3 w-3 mr-1" />,
      phantom: <Layers className="h-3 w-3 mr-1" />,
    };

    return (
      <Badge variant="outline" className="flex items-center gap-1">
        {icons[type]}
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Bills of Material (BOMs)</h1>
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              Phase 2
            </Badge>
          </div>
          <p className="text-muted-foreground mt-2">
            Manage product structures, component lists, and manufacturing recipes
          </p>
        </div>
        <HelpButton onClick={openHelp} />
      </div>

      {/* Metrics */}
      <div className="grid gap-4 md:grid-cols-5">
        <StaticMetricCardAlt
          title="Total BOMs"
          value={metrics.total.toString()}
          change="+0%"
          changeLabel="from last month"
          icon={FileText}
          variant="info"
        />
        <StaticMetricCardAlt
          title="Active BOMs"
          value={metrics.active.toString()}
          change="+12%"
          changeLabel="from last month"
          icon={CheckCircle}
          variant="success"
        />
        <StaticMetricCardAlt
          title="Draft BOMs"
          value={metrics.draft.toString()}
          change="+0%"
          changeLabel="from last month"
          icon={Clock}
          variant="warning"
        />
        <StaticMetricCardAlt
          title="Avg Components"
          value={metrics.avgComponents}
          change="+0%"
          changeLabel="from last month"
          icon={Package}
          variant="info"
        />
        <StaticMetricCardAlt
          title="Multi-Level"
          value={metrics.multiLevel.toString()}
          change="+0%"
          changeLabel="from last month"
          icon={Layers}
          variant="info"
        />
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>BOM List</CardTitle>
              <CardDescription>
                {filteredBOMs.length} of {MOCK_BOMS.length} BOMs
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsColumnsOpen(true)}>
                <Settings2 className="h-4 w-4 mr-2" />
                Columns
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <Download className="h-4 w-4 mr-2" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportExcel}>
                <Download className="h-4 w-4 mr-2" />
                Excel
              </Button>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                New BOM
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filters */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search BOMs by number, item code, or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="manufacturing">Manufacturing</SelectItem>
                  <SelectItem value="assembly">Assembly</SelectItem>
                  <SelectItem value="kit">Kit</SelectItem>
                  <SelectItem value="phantom">Phantom</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsFiltersOpen(true)}
              >
                <Filter className="h-4 w-4 mr-2" />
                Advanced
              </Button>
            </div>

            {/* Active Filter Chips */}
            {activeFilterChips.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-muted-foreground">Active filters:</span>
                {activeFilterChips.map((chip) => (
                  <Badge
                    key={chip.id}
                    variant="secondary"
                    className="gap-1 pr-1"
                  >
                    {chip.displayText}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 hover:bg-transparent"
                      onClick={() => {
                        if (chip.field === "status") {
                          setStatusFilter("all");
                        } else if (chip.field === "type") {
                          setTypeFilter("all");
                        } else {
                          setFilterRows(prev => prev.filter(row => row.id !== chip.id));
                        }
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setStatusFilter("all");
                    setTypeFilter("all");
                    setFilterRows([]);
                  }}
                >
                  Clear all
                </Button>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="mt-6 border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  {columnConfig.visibleColumns.includes("bomNumber") && <TableHead>BOM Number</TableHead>}
                  {columnConfig.visibleColumns.includes("parentItemCode") && <TableHead>Parent Item</TableHead>}
                  {columnConfig.visibleColumns.includes("parentItemName") && <TableHead>Description</TableHead>}
                  {columnConfig.visibleColumns.includes("version") && <TableHead>Version</TableHead>}
                  {columnConfig.visibleColumns.includes("status") && <TableHead>Status</TableHead>}
                  {columnConfig.visibleColumns.includes("bomType") && <TableHead>Type</TableHead>}
                  {columnConfig.visibleColumns.includes("totalCost") && <TableHead className="text-right">Total Cost</TableHead>}
                  {columnConfig.visibleColumns.includes("componentCount") && <TableHead className="text-right">Components</TableHead>}
                  {columnConfig.visibleColumns.includes("effectiveDate") && <TableHead>Effective Date</TableHead>}
                  {columnConfig.visibleColumns.includes("usageCount") && <TableHead className="text-right">Usage</TableHead>}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBOMs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                      No BOMs found matching your criteria
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredBOMs.map((bom) => (
                    <TableRow
                      key={bom.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleViewBOM(bom)}
                    >
                      {columnConfig.visibleColumns.includes("bomNumber") && (
                        <TableCell className="font-medium">{bom.bomNumber}</TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("parentItemCode") && (
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {bom.isMultiLevel && (
                              <Layers className="h-4 w-4 text-blue-500" title="Multi-level BOM" />
                            )}
                            {bom.parentItemCode}
                          </div>
                        </TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("parentItemName") && (
                        <TableCell>
                          <div>
                            <div className="font-medium">{bom.parentItemName}</div>
                            <div className="text-sm text-muted-foreground">{bom.parentItemDescription}</div>
                          </div>
                        </TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("version") && (
                        <TableCell>{bom.version}</TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("status") && (
                        <TableCell>{getStatusBadge(bom.status)}</TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("bomType") && (
                        <TableCell>{getTypeBadge(bom.bomType)}</TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("totalCost") && (
                        <TableCell className="text-right font-medium">
                          £{bom.totalCost.toFixed(2)}
                        </TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("componentCount") && (
                        <TableCell className="text-right">{bom.components.length}</TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("effectiveDate") && (
                        <TableCell>{bom.effectiveDate.toLocaleDateString()}</TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("usageCount") && (
                        <TableCell className="text-right">{bom.usageCount || 0}</TableCell>
                      )}
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewBOM(bom);
                          }}
                        >
                          View
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* BOM Details Drawer */}
      <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto">
          {selectedBOM && (
            <>
              <SheetHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <SheetTitle className="text-2xl">{selectedBOM.bomNumber}</SheetTitle>
                    <SheetDescription>
                      {selectedBOM.parentItemCode} - {selectedBOM.parentItemName}
                    </SheetDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(selectedBOM.status)}
                    {getTypeBadge(selectedBOM.bomType)}
                  </div>
                </div>
              </SheetHeader>

              <Tabs defaultValue="overview" className="mt-6">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="components">Components</TabsTrigger>
                  <TabsTrigger value="routing">Routing</TabsTrigger>
                  <TabsTrigger value="costing">Costing</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>BOM Details</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground">BOM Number</Label>
                          <div className="font-medium">{selectedBOM.bomNumber}</div>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Version</Label>
                          <div className="font-medium">{selectedBOM.version}</div>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Parent Item</Label>
                          <div className="font-medium">{selectedBOM.parentItemCode}</div>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Base Quantity</Label>
                          <div className="font-medium">
                            {selectedBOM.baseQuantity} {selectedBOM.unitOfMeasure}
                          </div>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Effective Date</Label>
                          <div className="font-medium">{selectedBOM.effectiveDate.toLocaleDateString()}</div>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Expiry Date</Label>
                          <div className="font-medium">
                            {selectedBOM.expiryDate ? selectedBOM.expiryDate.toLocaleDateString() : "N/A"}
                          </div>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Created By</Label>
                          <div className="font-medium">{selectedBOM.createdBy}</div>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Approved By</Label>
                          <div className="font-medium">{selectedBOM.approvedBy || "Not approved"}</div>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Usage Count</Label>
                          <div className="font-medium">{selectedBOM.usageCount || 0} times</div>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Last Used</Label>
                          <div className="font-medium">
                            {selectedBOM.lastUsedDate ? selectedBOM.lastUsedDate.toLocaleDateString() : "Never"}
                          </div>
                        </div>
                      </div>
                      {selectedBOM.notes && (
                        <div>
                          <Label className="text-muted-foreground">Notes</Label>
                          <div className="text-sm mt-1">{selectedBOM.notes}</div>
                        </div>
                      )}
                      {selectedBOM.isMultiLevel && (
                        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <Info className="h-4 w-4 text-blue-600" />
                          <span className="text-sm text-blue-900">
                            This is a multi-level BOM containing sub-assemblies
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Components Tab */}
                <TabsContent value="components" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Component List ({selectedBOM.components.length})</CardTitle>
                      <CardDescription>
                        Materials and components required to build {selectedBOM.baseQuantity} {selectedBOM.unitOfMeasure}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Line</TableHead>
                            <TableHead>Component Code</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead className="text-right">Qty</TableHead>
                            <TableHead>UOM</TableHead>
                            <TableHead className="text-right">Unit Cost</TableHead>
                            <TableHead className="text-right">Total Cost</TableHead>
                            <TableHead className="text-right">Available</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedBOM.components.map((component) => (
                            <TableRow key={component.id}>
                              <TableCell>{component.lineNumber}</TableCell>
                              <TableCell className="font-medium">
                                <div className="flex items-center gap-2">
                                  {component.isOptional && (
                                    <Badge variant="outline" className="text-xs">Optional</Badge>
                                  )}
                                  {component.componentCode}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <div className="font-medium">{component.componentName}</div>
                                  <div className="text-sm text-muted-foreground">{component.componentDescription}</div>
                                  {component.notes && (
                                    <div className="text-xs text-muted-foreground mt-1">{component.notes}</div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right">{component.quantity}</TableCell>
                              <TableCell>{component.unitOfMeasure}</TableCell>
                              <TableCell className="text-right">£{component.unitCost.toFixed(2)}</TableCell>
                              <TableCell className="text-right font-medium">£{component.totalCost.toFixed(2)}</TableCell>
                              <TableCell className="text-right">
                                <Badge
                                  variant={component.availableQuantity && component.availableQuantity >= component.quantity ? "default" : "destructive"}
                                  className={component.availableQuantity && component.availableQuantity >= component.quantity ? "bg-green-100 text-green-800" : ""}
                                >
                                  {component.availableQuantity || 0}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Routing Tab */}
                <TabsContent value="routing" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Manufacturing Routing</CardTitle>
                      <CardDescription>
                        Operations and work centers required for production
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {selectedBOM.routing && selectedBOM.routing.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Op #</TableHead>
                              <TableHead>Operation</TableHead>
                              <TableHead>Work Center</TableHead>
                              <TableHead className="text-right">Setup (min)</TableHead>
                              <TableHead className="text-right">Run Time</TableHead>
                              <TableHead className="text-right">Labor Rate</TableHead>
                              <TableHead className="text-right">Overhead Rate</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selectedBOM.routing.map((operation) => (
                              <TableRow key={operation.id}>
                                <TableCell>{operation.operationNumber}</TableCell>
                                <TableCell>
                                  <div>
                                    <div className="font-medium">{operation.operationName}</div>
                                    {operation.description && (
                                      <div className="text-sm text-muted-foreground">{operation.description}</div>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>{operation.workCenter}</TableCell>
                                <TableCell className="text-right">{operation.setupTimeMinutes}</TableCell>
                                <TableCell className="text-right">{operation.runTimePerUnit} min/unit</TableCell>
                                <TableCell className="text-right">£{operation.laborCostPerHour.toFixed(2)}/hr</TableCell>
                                <TableCell className="text-right">£{operation.overheadCostPerHour.toFixed(2)}/hr</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <Wrench className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>No routing operations defined</p>
                          <p className="text-sm">This BOM does not require manufacturing operations</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Costing Tab */}
                <TabsContent value="costing" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Cost Breakdown</CardTitle>
                      <CardDescription>
                        Detailed cost analysis for {selectedBOM.baseQuantity} {selectedBOM.unitOfMeasure}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Summary */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="text-sm text-muted-foreground">Material Cost</div>
                          <div className="text-2xl font-bold">£{selectedBOM.totalMaterialCost.toFixed(2)}</div>
                        </div>
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                          <div className="text-sm text-muted-foreground">Labor Cost</div>
                          <div className="text-2xl font-bold">£{selectedBOM.totalLaborCost.toFixed(2)}</div>
                        </div>
                        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                          <div className="text-sm text-muted-foreground">Overhead Cost</div>
                          <div className="text-2xl font-bold">£{selectedBOM.totalOverheadCost.toFixed(2)}</div>
                        </div>
                        <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                          <div className="text-sm text-muted-foreground">Total Cost</div>
                          <div className="text-2xl font-bold">£{selectedBOM.totalCost.toFixed(2)}</div>
                        </div>
                      </div>

                      {/* Cost per unit */}
                      <div className="p-4 bg-gray-50 border rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm text-muted-foreground">Cost Per Unit</div>
                            <div className="text-xl font-bold">£{selectedBOM.costPerUnit.toFixed(2)}</div>
                          </div>
                          <DollarSign className="h-8 w-8 text-muted-foreground" />
                        </div>
                      </div>

                      {/* Component cost breakdown */}
                      <div>
                        <h4 className="font-semibold mb-3">Component Costs</h4>
                        <div className="space-y-2">
                          {selectedBOM.components.map((component) => {
                            const percentage = (component.totalCost / selectedBOM.totalMaterialCost) * 100;
                            return (
                              <div key={component.id} className="flex items-center gap-3">
                                <div className="flex-1">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium">{component.componentCode}</span>
                                    <span className="text-sm font-medium">£{component.totalCost.toFixed(2)}</span>
                                  </div>
                                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-blue-500"
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                </div>
                                <span className="text-sm text-muted-foreground w-12 text-right">
                                  {percentage.toFixed(1)}%
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              {/* Action Buttons */}
              <div className="mt-6 flex items-center gap-2">
                <Button variant="outline" className="flex-1">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit BOM
                </Button>
                <Button variant="outline" className="flex-1">
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicate
                </Button>
                <Button variant="outline" className="flex-1">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Advanced Filters Modal */}
      <AdvancedFiltersModal
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        filterRows={filterRows}
        setFilterRows={setFilterRows}
        filterLogic={filterLogic}
        setFilterLogic={setFilterLogic}
        availableFields={[
          { key: "bomNumber", label: "BOM Number", type: "text" },
          { key: "parentItemCode", label: "Parent Item Code", type: "text" },
          { key: "parentItemName", label: "Parent Item Name", type: "text" },
          { key: "status", label: "Status", type: "select", options: ["active", "draft", "inactive", "archived"] },
          { key: "bomType", label: "Type", type: "select", options: ["manufacturing", "assembly", "kit", "phantom"] },
          { key: "totalCost", label: "Total Cost", type: "number" },
          { key: "effectiveDate", label: "Effective Date", type: "date" },
          { key: "usageCount", label: "Usage Count", type: "number" },
        ]}
      />

      {/* Configure Columns Modal */}
      <ConfigureColumnsModal
        open={isColumnsOpen}
        onOpenChange={setIsColumnsOpen}
        columns={columnDefinitions}
        currentConfig={columnConfig}
        onSave={setColumnConfig}
        defaultConfig={{
          visibleColumns: DEFAULT_VISIBLE_COLUMNS,
          columnOrder: DEFAULT_VISIBLE_COLUMNS,
        }}
      />

      {/* Help Drawer */}
      <ContextualHelpDrawer
        isOpen={isHelpOpen}
        onClose={closeHelp}
        title="Bills of Material (BOMs)"
        sections={[
          {
            title: "Overview",
            content: "Bills of Material (BOMs) define the structure of products, listing all components, materials, and operations required for manufacturing or assembly.",
          },
          {
            title: "BOM Types",
            content: "Manufacturing BOMs include routing operations. Assembly BOMs are simpler structures. Kits are pre-packaged sets. Phantom BOMs are used for temporary assemblies.",
          },
          {
            title: "Multi-Level BOMs",
            content: "Multi-level BOMs contain components that are themselves assemblies, creating a hierarchical product structure.",
          },
          {
            title: "Cost Calculation",
            content: "Total cost includes material costs (components), labor costs (operations), and overhead costs. The system automatically calculates cost per unit.",
          },
        ]}
      />
    </div>
  );
}
